import React from "react";
import { PeekBox, PeekChip, PeekLink, PeekSkeleton, PeekStack, PeekTypography, useTheme, } from "@piiqtechnologies/ui-components";
import Award from "@piiqtechnologies/svg-icons/lib/Award";
import ExternalLink from "@piiqtechnologies/svg-icons/lib/ExternalLink";
import { getScoreStatusFromValue } from "@utils/scoring";
export const RankBadge = ({ label, size = "medium" }) => {
    const theme = useTheme();
    return (React.createElement(PeekChip, { label: label, size: size, sx: {
            height: "100%",
            borderRadius: 1,
            backgroundColor: "transparent",
            color: theme.palette.grey[400],
            fontWeight: theme.typography.fontWeightLight,
            minWidth: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            "&.MuiChip-root": {
                backgroundColor: "transparent",
            },
            "& .MuiChip-label": {
                px: 1,
                pt: 1,
                fontSize: theme.typography.h3.fontSize,
                height: "100%",
                fontWeight: theme.typography.fontWeightLight,
            },
        } }));
};
export const MedalIcon = ({ "aria-label": ariaLabel }) => {
    const theme = useTheme();
    return (React.createElement(PeekBox, { component: "span", "aria-label": ariaLabel, sx: {
            fontSize: "1.25rem",
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            "& svg path:first-of-type": { fill: theme.palette.orange[500] },
        } },
        React.createElement(Award, { width: 20, height: 20, style: { color: theme.palette.orange[500] } })));
};
export const ScorePill = ({ score, size = "medium", label, invisibleLabel }) => {
    const theme = useTheme();
    const hasScore = score != null && score >= 0;
    if (invisibleLabel != null) {
        return (React.createElement(PeekChip, { label: invisibleLabel, size: size, variant: "outlined", sx: {
                height: 26,
                borderRadius: 30,
                backgroundColor: theme.palette.common.white,
                color: theme.palette.grey[400],
                border: `1px solid ${theme.palette.grey[300]}`,
                fontWeight: theme.typography.fontWeightMedium,
                fontSize: theme.typography.body1.fontSize,
                "& .MuiChip-label": {
                    px: 1.5,
                },
            } }));
    }
    // Bracket color matches the home page score chips, computed from the rounded value shown in the pill.
    const status = hasScore ? getScoreStatusFromValue(Math.round(score)) : undefined;
    return (React.createElement(PeekChip, { label: hasScore ? `${Math.round(score)}` : (label !== null && label !== void 0 ? label : "N/A"), size: size, "data-score-status": status, sx: {
            height: 26,
            borderRadius: 30,
            "&.MuiChip-filledDefault": {
                backgroundColor: status ? theme.palette[status].main : theme.palette.grey[300],
            },
            color: theme.palette.common.white,
            border: "none",
            fontWeight: theme.typography.fontWeightMedium,
            fontSize: theme.typography.body1.fontSize,
            "& .MuiChip-label": {
                px: 1.5,
            },
        } }));
};
const SourceName = ({ source, urls }) => {
    var _a;
    const href = ((_a = urls === null || urls === void 0 ? void 0 : urls[0]) === null || _a === void 0 ? void 0 : _a.url) || (source ? `https://${source}` : null);
    if (href) {
        return (React.createElement(PeekLink, { href: href, target: "_blank", rel: "noopener noreferrer", sx: { display: "inline-flex", alignItems: "center", gap: 0.5 } },
            source,
            React.createElement(ExternalLink, { width: 12, height: 12 })));
    }
    return React.createElement(React.Fragment, null, source);
};
const Details = ({ bestPerforming, mostCited, bestPerformingLabel, mostCitedLabel, emptyValueLabel, }) => {
    const bestPerformingText = bestPerforming
        .slice(0, 3)
        .map((s) => s.subcategory)
        .join(", ") || emptyValueLabel;
    const topCitedSources = mostCited.slice(0, 3);
    return (React.createElement(PeekStack, { spacing: 0.5, sx: { minWidth: 0 } },
        React.createElement(PeekTypography, { variant: "body1", color: "text.primary" }, bestPerformingLabel.replace("{features}", bestPerformingText)),
        React.createElement(PeekTypography, { variant: "body1", color: "text.primary" },
            mostCitedLabel,
            " ",
            topCitedSources.length > 0
                ? topCitedSources.map((s, i, arr) => (React.createElement(PeekBox, { component: "span", key: `${s.source}-${i}` },
                    React.createElement(SourceName, { source: s.source, urls: s.urls }),
                    i < arr.length - 1 && ", ")))
                : emptyValueLabel)));
};
// Placeholder for the two Details lines while their data is still loading: the labels
// render immediately so the row template stays populated, with a pulse bar in the value slot.
const DetailsSkeleton = ({ bestPerformingLabel, mostCitedLabel, }) => (React.createElement(PeekStack, { spacing: 0.5, "data-testid": "details-skeleton", sx: { minWidth: 0 } }, [
    { label: bestPerformingLabel.replace("{features}", "").trimEnd(), width: 140 },
    { label: mostCitedLabel, width: 180 },
].map(({ label, width }) => (React.createElement(PeekStack, { key: label, direction: "row", alignItems: "center", spacing: 1 },
    React.createElement(PeekTypography, { variant: "body1", color: "text.primary" }, label),
    React.createElement(PeekSkeleton, { variant: "text", width: width, height: 20 }))))));
export const RankingRow = ({ rank, name, appearanceRate, bestPerforming = [], mostCited = [], leading, showYou, bestPerformingLabel, mostCitedLabel, youLabel = "You", scoreNotAvailableLabel, rankNotAvailableLabel = "N/A", invisibleLabel, emptyValueLabel, trailingAction, showDetails = true, detailsLoading, }) => {
    const detailsProps = { bestPerforming, mostCited, bestPerformingLabel, mostCitedLabel, emptyValueLabel };
    return (React.createElement(PeekBox, { component: "li" },
        React.createElement(PeekStack, { direction: "row", spacing: 2, alignItems: "stretch" },
            React.createElement(PeekBox, { sx: { display: "flex", alignItems: "center", minWidth: 80 } },
                React.createElement(RankBadge, { label: rank != null ? `#${rank}` : rankNotAvailableLabel, size: "small" })),
            React.createElement(PeekBox, { sx: { flexGrow: 1 } },
                React.createElement(PeekStack, { spacing: 1 },
                    React.createElement(PeekStack, { direction: "row", alignItems: "center", spacing: 1.5 },
                        React.createElement(PeekStack, { direction: "row", alignItems: "center", spacing: 1, sx: { flexGrow: 1, minWidth: 0 } },
                            leading,
                            React.createElement(PeekTypography, { variant: "h4", fontWeight: "fontWeightMedium" }, name)),
                        showYou && (React.createElement(PeekChip, { label: youLabel, size: "small", variant: "outlined", color: "secondary", sx: { fontWeight: "medium" } })),
                        React.createElement(ScorePill, { score: appearanceRate, size: "small", label: scoreNotAvailableLabel, invisibleLabel: invisibleLabel })),
                    detailsLoading ? (React.createElement(DetailsSkeleton, { bestPerformingLabel: bestPerformingLabel, mostCitedLabel: mostCitedLabel })) : trailingAction ? (React.createElement(PeekStack, { direction: "row", justifyContent: "space-between", alignItems: "flex-end", spacing: 1 },
                        showDetails && React.createElement(Details, Object.assign({}, detailsProps)),
                        React.createElement(PeekBox, { sx: { flexShrink: 0 } }, trailingAction))) : (showDetails && React.createElement(Details, Object.assign({}, detailsProps))))))));
};
export default RankingRow;
